// ─────────────────────────────────────────────────────────────────────────────
// TerminalState — rendered for status in {completed, expired, cancelled, failed}.
//
// One shared shell, four configurations. Kept as a single component because
// the visual scaffolding is identical (card + icon + heading + description
// + optional kv row + optional return action) — only the content and
// visual tint differ. Splitting into four files would duplicate markup
// for no testability or maintenance win.
//
// Per-status content chosen per v7 preview and reviewed in session:
//
//   completed  → green checkmark, "Payment received" + confirmed timestamp.
//                Back-to-merchant CTA uses PRIMARY orange treatment because
//                the payer's journey is successfully done — push them forward.
//
//   expired    → neutral hourglass, "This checkout has expired" + guidance
//                to return to merchant and start a new checkout. Secondary
//                back link (not primary — the journey failed, we don't
//                celebrate).
//
//   cancelled  → neutral X-in-circle, "This payment was cancelled" with
//                explanation that no money moved. Secondary back link.
//
//   failed     → AMBER (not red) warning triangle, "We couldn't set up this
//                payment" with calming wording. No money moved, user can
//                try again. Secondary back link.
//
// Merchant name is woven into each description so the message is specific
// to who they were paying, not generic boilerplate.
//
// "Back to merchant" action is conditional on data.returnUrl being present.
// Null returnUrl → no action shown (we don't know where "back" is).
// ─────────────────────────────────────────────────────────────────────────────

import type { CheckoutStatus, PublicCheckoutResponse } from '../types';

export interface TerminalStateProps {
  status: Exclude<CheckoutStatus, 'pending'>;
  data: PublicCheckoutResponse;
}

export function TerminalState({ status, data }: TerminalStateProps) {
  const merchant = data.merchantDisplayName;
  const returnUrl = data.returnUrl;

  return (
    <div className={`state-card ${status}`}>
      <div className="state-icon-wrap">
        <StatusIcon status={status} />
      </div>

      <h1 className="state-heading">{HEADING[status]}</h1>

      <p className="state-desc">{description(status, merchant, data.amountDisplay)}</p>

      {status === 'completed' && (
        <div className="state-kv">
          <span className="state-kv-label">Confirmed</span>
          <span className="state-kv-value">{formatConfirmedAt(data)}</span>
        </div>
      )}

      {returnUrl !== null && (
        <a
          href={returnUrl}
          className={`state-action ${
            status === 'completed' ? 'primary' : 'secondary'
          }`}
          rel="noopener"
        >
          {status === 'completed' ? (
            <>
              Back to merchant
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M5 12h14M12 5l7 7-7 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M19 12H5M12 19l-7-7 7-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to merchant
            </>
          )}
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-status heading copy. Kept as a flat object so any wording change
// lives in one place and is obvious to future readers.
// ─────────────────────────────────────────────────────────────────────────────

const HEADING: Record<Exclude<CheckoutStatus, 'pending'>, string> = {
  completed: 'Payment received',
  expired: 'This checkout has expired',
  cancelled: 'This payment was cancelled',
  failed: "We couldn't set up this payment",
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-status description. Weaves in the merchant name and, where available,
// the amount so the message feels specific to this transaction.
// ─────────────────────────────────────────────────────────────────────────────

function description(
  status: Exclude<CheckoutStatus, 'pending'>,
  merchant: string,
  amountDisplay: string | null
): string {
  switch (status) {
    case 'completed':
      return amountDisplay
        ? `Your payment of ${amountDisplay} to ${merchant} has been confirmed. The merchant has been notified.`
        : `Your payment to ${merchant} has been confirmed. The merchant has been notified.`;
    case 'expired':
      return `The one-time account is no longer active. If you still need to complete this payment, please return to ${merchant} and start a new checkout.`;
    case 'cancelled':
      return `${merchant} cancelled this checkout. No money has been moved. If you weren't expecting this, please contact the merchant.`;
    case 'failed':
      return `Something went wrong while preparing your checkout with ${merchant}. No money has been moved. Please return to the merchant and try again, or contact them if the problem continues.`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Format the confirmation timestamp for the "Confirmed" kv row.
//
// We use createdAt as a best-available proxy today. When the BFF exposes
// a dedicated confirmedAt field (matches payment_intents.confirmed_at in
// bank-rail), this switches to that value. Carry-forward.
// ─────────────────────────────────────────────────────────────────────────────

function formatConfirmedAt(data: PublicCheckoutResponse): string {
  // TODO(bff-contract): use data.confirmedAt when the BFF exposes it.
  // For now, createdAt + "just now" feel is the honest fallback.
  const d = new Date(data.createdAt);
  // Nigerian English locale with short date/time
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  try {
    return new Intl.DateTimeFormat('en-NG', options).format(d);
  } catch {
    // Ultra-defensive fallback if Intl is unavailable (ancient browsers).
    return d.toLocaleString();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-status icon glyphs. Inline SVG; stroke inherits currentColor which
// is set by the per-state CSS class on .state-icon-wrap.
// ─────────────────────────────────────────────────────────────────────────────

function StatusIcon({
  status,
}: {
  status: Exclude<CheckoutStatus, 'pending'>;
}) {
  switch (status) {
    case 'completed':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case 'expired':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 2h12M6 22h12M6 2v4a6 6 0 0 0 12 0V2M6 22v-4a6 6 0 0 1 12 0v4" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M15 9l-6 6M9 9l6 6" />
        </svg>
      );
    case 'failed':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3L2 21h20L12 3z" />
          <path d="M12 10v5M12 18h.01" />
        </svg>
      );
  }
}
